import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as _ from 'lodash';
import { GlobalConstants } from 'src/app/constant/constant';
import { FieldActivity } from 'src/app/entities/FieldActivity';
import { FIPSState } from 'src/app/entities/FIPSState';
import { LaborCategory } from 'src/app/entities/LaborCategory';
import { Project } from 'src/app/entities/Project';
import { Task } from 'src/app/entities/Task';
import { User } from 'src/app/entities/User';
import { WorkAssignment } from 'src/app/entities/WorkAssignment';
import { TOAST_CLASS, TOAST_DURATION } from 'src/app/enums/notification/notification.enum';
import { AssignmentService } from 'src/app/services/assignment.service';
import { FIPSStateService } from 'src/app/services/fips-state.service';
import { LaborCategoryService } from 'src/app/services/labor-category.service';
import { LocationService } from 'src/app/services/location.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { ProjectService } from 'src/app/services/project.service';
import { TaskService } from 'src/app/services/task.service';
import { UserService } from 'src/app/services/user.service';
import { IonSelectSearchableComponent } from 'src/app/shared/common/ionSelectSearchable/ionSelectSearchable.component';
dayjs.extend(utc);
dayjs.extend(timezone);

enum TaskType {
  APN = 'APN',
  Community = 'Community',
}

@Component({
  selector: 'app-project-task-selection',
  templateUrl: 'projectTaskSelection.component.html',
  styleUrls: ['./projectTaskSelection.component.scss'],
})
export class ProjectTaskSelectionComponent implements OnInit, OnChanges {
  @Input() userId: number;
  @Input() assignment: WorkAssignment;
  @Input() selectedDate: string = dayjs().format('YYYY-MM-DD');
  @Input() disabled = false;
  @Output() assignmentSelected = new EventEmitter<{ assignment: WorkAssignment | undefined; isValid: boolean }>();
  @Output() loadingComplete = new EventEmitter();

  selectedProject: Project | null;
  selectedTask: Task | null;
  selectedFieldActivity: FieldActivity | null;
  selectedLaborCategory: LaborCategory | null;
  selectedFipsState: FIPSState | null;
  taskTypeFilter: TaskType | null;
  showTaskTypeFilterButtons: boolean = false;
  useFieldActivities: boolean = true;
  isLoading: boolean = true;
  isProjectsLoading: boolean = true;
  isTasksLoading: boolean = false;
  isFieldActivitiesLoading: boolean = false;
  isLaborCategoriesLoading: boolean = false;
  isFipsStatesLoading: boolean = true;
  private user: User;
  private projects: Project[] = [];
  private tasks: Task[] = [];
  private fieldActivities: FieldActivity[] = [];
  private laborCategories: LaborCategory[] = [];
  private fipsStates: FIPSState[] = [];
  readonly TaskType = TaskType;

  constructor(
    private assignmentService: AssignmentService,
    private projectService: ProjectService,
    private modalController: ModalController,
    private userService: UserService,
    private fipsStateService: FIPSStateService,
    private taskService: TaskService,
    private laborCategoryService: LaborCategoryService,
    private locationService: LocationService,
    private notificationService: NotificationsService
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.user = await this.userService.getUserByOnlineId(this.userId);

    await this.loadData();

    if (this.assignment) {
      await this.setSelectedByAssignment();
    }

    if (!this.selectedFipsState) {
      await this.setFipsStateFromLocation();
    }

    this.isLoading = false;
    this.loadingComplete.emit();
    this.emitWorkAssignmentChange();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedDate && !changes.selectedDate.firstChange) {
      this.selectedDate = changes.selectedDate.currentValue;
      await this.loadData();
    }
  }

  async emitWorkAssignmentChange(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    const isAssignmentValid = await this.checkIsWorkAssignmentValid();
    this.assignmentSelected.emit({
      assignment: {
        ProjectId: this.selectedProject?.Id,
        TaskId: this.selectedTask?.Id,
        LaborCategoryId: this.selectedLaborCategory?.Id,
        FIPSStateId: this.selectedFipsState?.Id,
        FieldActivityId: this.selectedFieldActivity?.Id,
      },
      isValid: isAssignmentValid,
    });
  }

  private async loadData() {
    await this.loadProjects();
    await this.loadFipsStates();
  }

  private async loadProjects(): Promise<void> {
    this.isProjectsLoading = true;
    let result = await this.projectService.getActiveAssignedProjects(this.user.Id, this.selectedDate);

    if (result.length > 0) {
      result = _(result)
        .orderBy(x => x.NameAndNumber.toLowerCase(), ['asc'])
        .value();
    }

    this.projects = result;
    if (this.projects.length === 1) {
      await this.setProject(this.projects[0]);
    }
    this.isProjectsLoading = false;
  }

  private async loadTasks(projectId: number): Promise<void> {
    this.isTasksLoading = true;
    let result = await this.taskService.getAllActiveChargeableTasksByProjectId(projectId, this.selectedDate);

    if (result.length > 0) {
      result = _(result)
        .orderBy(x => x.NameAndNumber.toLowerCase(), ['asc'])
        .value();
    }

    this.tasks = result;
    if (this.tasks.length === 1) {
      this.setTask(this.tasks[0]);
    }

    this.showTaskTypeFilterButtons = this.loadShowTaskTypeFilterButtons();
    this.isTasksLoading = false;
  }

  private async loadLaborCategories(projectId: number, enableLaborCategoryAssignment: boolean): Promise<void> {
    this.isLaborCategoriesLoading = true;
    let result = await this.laborCategoryService.getAllActiveValidLaborCategoriesByProjectId(projectId, this.selectedDate, this.user.EmployeeNumber, enableLaborCategoryAssignment);

    if (result.length > 0) {
      result = _(result)
        .orderBy(x => x.NameAndNumber.toLowerCase(), ['asc'])
        .value();
    }

    this.laborCategories = result;
    if (this.laborCategories.length === 1) {
      this.setLaborCategory(this.laborCategories[0]);
    }
    this.isLaborCategoriesLoading = false;
  }

  private async loadFieldActivities(projectId: number): Promise<void> {
    this.isFieldActivitiesLoading = true;
    let result = await this.projectService.getActiveAssignedFieldActivitiesByUserIdProjectId(this.user.Id, projectId, this.selectedDate);

    if (result.length > 0) {
      result = _(result)
        .orderBy(x => x.ActivityName.toLowerCase(), ['asc'])
        .value();
    }

    this.fieldActivities = result;
    if (this.fieldActivities.length === 1) {
      this.setFieldActivity(this.fieldActivities[0]);
    }
    this.isFieldActivitiesLoading = false;
  }

  private async loadFipsStates(): Promise<void> {
    this.isFipsStatesLoading = true;
    let result = await this.fipsStateService.getAllFIPSStates();

    if (result.length > 0) {
      result = _(result)
        .orderBy(x => x.Name.toLowerCase(), ['asc'])
        .value();
    }

    this.fipsStates = result;
    if (this.fipsStates.length === 1) {
      this.setFipsState(this.fipsStates[0]);
    }
    this.isFipsStatesLoading = false;
  }

  async setProject(project: Project) {
    if (this.selectedProject?.Id !== project.Id) {
      this.selectedProject = project;

      this.loadUseFieldActivities();
      this.setTaskFilterType(null);
      this.setTask(null);
      this.setLaborCategory(null);
      this.setFieldActivity(null);

      await this.loadTasks(project.Id);
      await this.loadLaborCategories(project.Id, project.EnableLaborCategoryAssignment);

      if (this.useFieldActivities) {
        await this.loadFieldActivities(project.Id);
      }

      this.emitWorkAssignmentChange();
    }
  }

  setTask(task: Task) {
    this.selectedTask = task;
    this.emitWorkAssignmentChange();
  }

  setFieldActivity(fieldActivity: FieldActivity) {
    this.selectedFieldActivity = fieldActivity;

    if (fieldActivity !== null) {
      const task = this.tasks.find(t => t.Id === fieldActivity.TaskId);
      if (task) {
        this.setTask(task);
      } else {
        this.displayErrorToast('The Task associated with the selected Position is not valid. Please contact your supervisor.');
      }

      const laborCategory = this.laborCategories.find(lc => lc.Id === fieldActivity.LaborCategoryId);
      if (laborCategory) {
        this.setLaborCategory(laborCategory);
      } else {
        this.displayErrorToast('The Labor Category associated with the selected Position is not valid. Please contact your supervisor.');
      }
    }

    this.emitWorkAssignmentChange();
  }

  setLaborCategory(laborCategory: LaborCategory) {
    this.selectedLaborCategory = laborCategory;
    this.emitWorkAssignmentChange();
  }

  setFipsState(fipsState: FIPSState) {
    this.selectedFipsState = fipsState;
    this.emitWorkAssignmentChange();
  }

  async setFipsStateFromLocation() {
    let currentStateName = await this.locationService.getCurrentStateName();
    if (currentStateName) {
      const fipsState = this.fipsStates.find(x => x.Name === currentStateName);
      this.setFipsState(fipsState);
    }
  }

  private async setSelectedByAssignment() {
    if (this.assignment.ProjectId) {
      const assignmentProject = this.projects.find(p => p.Id === this.assignment.ProjectId);
      if (assignmentProject) {
        await this.setProject(assignmentProject);
        const assignmentTask = this.tasks.find(task => task.Id === this.assignment.TaskId);
        if (assignmentTask) {
          this.setTask(assignmentTask);
          this.setTaskFilterType(assignmentTask.IsCommunity ? TaskType.Community : TaskType.APN);
        }

        const assignmentFieldActivity = this.fieldActivities.find(fieldActivity => fieldActivity.Id === this.assignment.FieldActivityId);
        if (assignmentFieldActivity) {
          await this.setFieldActivity(assignmentFieldActivity);
        }

        const assignmentLaborCategory = this.laborCategories.find(laborCategory => laborCategory.Id === this.assignment.LaborCategoryId);
        if (assignmentLaborCategory) {
          this.setLaborCategory(assignmentLaborCategory);
        }
      }
    }

    const assignmentFipsState = this.fipsStates.find(fipsState => fipsState.Id === this.assignment.FIPSStateId);
    if (assignmentFipsState) {
      this.setFipsState(assignmentFipsState);
    }
  }

  private async checkIsWorkAssignmentValid(): Promise<boolean> {
    if (!this.selectedProject || !this.selectedTask || !this.selectedLaborCategory || !this.selectedFipsState || !this.selectedDate) {
      return false;
    } else {
      return this.assignmentService.checkIsAssignmentValid(
        {
          ProjectId: this.selectedProject.Id,
          TaskId: this.selectedTask.Id,
          LaborCategoryId: this.selectedLaborCategory.Id,
          FIPSStateId: this.selectedFipsState.Id,
          FieldActivityId: this.selectedFieldActivity?.Id ?? null,
        },
        this.userId,
        this.selectedDate
      );
    }
  }

  async openModal<T>(values: object[], displayProperty: string, onSelectFn: (value: T) => void) {
    const modal = await this.modalController.create({
      component: IonSelectSearchableComponent,
      componentProps: {
        value: values,
        displayProperty: displayProperty,
        showSearchBoxCountGreaterThan: GlobalConstants.SEARCH_LIST_ITEM_DISPLAY_LIMIT,
      },
      cssClass: "dropdown-options-modal"
    });
    modal.onDidDismiss().then(dataReturned => {
      if (dataReturned.data) {
        onSelectFn(dataReturned.data);
      }
      modal.remove();
    });
    return modal.present();
  }

  async openProjectModal() {
    if (this.projects.length > 0) {
      this.openModal<Project>(this.projects, 'NameAndNumber', this.setProject.bind(this));
    } else {
      this.displayErrorToast('No projects are assigned and active for the current date. Please contact your supervisor.');
    }
  }

  async openTaskModal() {
    const filteredTasks = this.filterTaskByType(this.tasks, this.taskTypeFilter);
    if (filteredTasks.length > 0) {
      this.openModal<Task>( filteredTasks, 'NameAndNumber', this.setTask.bind(this));
    } else {
      this.displayErrorToast('No tasks are assigned and active for the selected project. Please contact your supervisor.');
    }
  }

  async openFieldActivityModal() {
    if (this.fieldActivities.length > 0) {
      this.openModal<FieldActivity>( this.fieldActivities, 'ActivityName', this.setFieldActivity.bind(this));
    } else {
      this.displayErrorToast('No positions are assigned and active for the selected project. Please contact your supervisor.');
    }
  }

  async openLaborCategoryModal() {
    if (this.laborCategories.length > 0) {
      this.openModal<LaborCategory>( this.laborCategories, 'NameAndNumber', this.setLaborCategory.bind(this));
    } else {
      this.displayErrorToast('No labor categories are assigned and active for the selected project. Please contact your supervisor.');
    }
  }

  async openFipsStateModal() {
    if (this.fipsStates.length > 0) {
      this.openModal<FIPSState>( this.fipsStates, 'Name', this.setFipsState.bind(this));
    } else {
      this.displayErrorToast('There was an error loading the work states. Please try logging in again. If the issue persists, please contact your supervisor.');
    }
  }

  private loadUseFieldActivities() {
    this.useFieldActivities = this.selectedProject && this.selectedProject.EnableFieldActivities && this.user.IsFieldWorker;
  }

  setTaskFilterType(type: TaskType | null) {
    this.taskTypeFilter = type;
    const filteredTasks = this.filterTaskByType(this.tasks, this.taskTypeFilter);
    if (filteredTasks.length === 1) {
      this.setTask(filteredTasks[0]);
    } else if (this.selectedTask && !filteredTasks.includes(this.selectedTask)) {
      this.setTask(null);
    }
  }

  loadShowTaskTypeFilterButtons() {
    return this.selectedProject.LBB && this.tasks.some(t => t.IsCommunity) && this.tasks.some(t => !t.IsCommunity);
  }

  filterTaskByType(tasks: Task[], selectedType: TaskType | null): Task[] {
    if (selectedType === TaskType.Community) {
      return tasks.filter(t => t.IsCommunity);
    } else if (selectedType === TaskType.APN) {
      return tasks.filter(t => !t.IsCommunity);
    } else {
      return tasks;
    }
  }

  displayErrorToast(message: string) {
    this.notificationService.showToast({ message, duration: TOAST_DURATION.MEDIUM, cssClass: TOAST_CLASS.DANGER });
  }
}
